import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { asString } from "@/components/shapes/shared/plain-record";
import { frameDisplayTitle } from "@/lib/frame-display-title";
import { frameProjectId } from "@/lib/frame-project";
import { cardPaint } from "../shared/card-paint";
import { FrameEmptyState } from "../shared/frame-empty-state";
import { ObjectFrame } from "../shared/object-frame";
import { useT } from "@/i18n";
import { framesChartMessages } from "@/i18n/messages/frames-chart";
import { ChartEmpty, ChartEmptyLine } from "./chart-empty";
import { ChartFace } from "./chart-face";
import {
  chartKindFrom,
  chartPointsFrom,
  chartSourceFrom,
} from "./chart-points";
import { ChartSourceBar } from "./chart-source-bar";
import { useChartRange } from "./use-chart-range";

/**
 * A chart: its card, larger, with every bar or point labelled and the
 * insight. A chart reading a Data frame names it and, with a date field,
 * offers a period; another period draws the server's points for it. With
 * nothing to draw yet it shows a ghost of its kind and when it fills.
 */
export function ChartFrame({ id, data }: FrameProps) {
  const t = useT(framesChartMessages);
  const content = data.content ?? {};
  const kind = chartKindFrom(content.kind);
  const source = chartSourceFrom(content.source);
  const projectId = frameProjectId(data);
  const period = useChartRange(projectId, source, content.data_revision);
  return (
    <ObjectFrame id={id} paint={cardPaint(data)} testId="chart-frame">
      <ChartFace
        kind={kind}
        title={asString(content.title) || frameDisplayTitle(data)}
        unit={asString(content.unit)}
        insight={asString(content.insight)}
        points={period.points ?? chartPointsFrom(content.points)}
        density="regular"
        opened
        testIdPrefix="chart-frame"
        empty={
          period.points ? (
            <p
              className="flex min-h-0 flex-1 items-center justify-center text-center text-appkit-small text-muted"
              data-testid="chart-frame-period-empty"
            >
              {t(period.failed ? "periodFailed" : "noDataInPeriod")}
            </p>
          ) : (
            <ChartEmpty kind={kind} testId="chart-frame-ghost">
              <FrameEmptyState
                frameId={id}
                status={data.status}
                testId="chart-frame-empty"
              >
                <ChartEmptyLine source={source} projectId={projectId} />
              </FrameEmptyState>
            </ChartEmpty>
          )
        }
        footer={source && <ChartSourceBar source={source} period={period} />}
      />
    </ObjectFrame>
  );
}
